// What tells you a generation failed, wherever you happen to be looking.
//
// IT EXISTS BECAUSE A FAILURE USED TO BE SILENT. A job that died took its card off the
// Workspace sixty seconds later and said nothing on the way out — no toast, no row, no
// record. Three generations failed that way on 2026-10-25 and the only place it was written
// down was the admin log. The person who asked for the video watched a card count up for
// twenty minutes and then watched it disappear.
//
// TOP RIGHT, AND IT WAITS. Not a toast that fades: a failure is news you have to act on
// (try again, pick another topic) and a message that removes itself after four seconds is a
// message you can miss by looking away. It stays until it is dismissed, and dismissing it
// removes the job from the queue — the same `removeJob` the × on the Workspace card calls,
// so the two surfaces cannot disagree about whether you have seen this.
//
// IT COUNTS RATHER THAN STACKS. Three failures in ten minutes is one banner saying three,
// not three banners pushing each other down the screen.
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useT } from '../i18n/useT';
import { failureReasonFor } from '../utils/constants';

const FailedJobBanner = ({ generationQueue }) => {
  const t = useT();
  const failed = (generationQueue?.queue || []).filter((j) => j.state === 'failed' && !j.silent);
  if (failed.length === 0) return null;

  const dismissAll = () => failed.forEach((j) => generationQueue?.removeJob(j.id));
  const newest = failed[failed.length - 1];

  return (
    /* `fixed`, above everything, and OUT OF THE WAY OF THE NAV. Top-right is where this app
       already puts transient state, and z-50 is the layer the queue panel uses — a notice
       that renders under a modal is a notice nobody reads. */
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-4 right-4 z-50 w-[min(92vw,380px)] rounded-2xl border border-amber-200
                 bg-white shadow-lg shadow-black/5 overflow-hidden"
    >
      <div className="flex items-start gap-3 p-4">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                         bg-amber-50 text-amber-600">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold leading-snug text-gray-900">
            {failed.length === 1
              ? t('ws_failed_title')
              : t('ws_failed_banner_many', { n: failed.length })}
          </p>
          {/* The TOPIC, when there is one. "A video failed" is a different sentence from
              "the video you asked about polynomials failed" — the second one tells you which
              of the three things you started is the one to redo. */}
          {failed.length === 1 && newest?.title && (
            <p className="mt-0.5 truncate text-[13px] text-gray-500" title={newest.title}>
              {newest.title}
            </p>
          )}
          {/* THE SAME ONE LINE THE CARD SHOWS, plus the reason — which is the one thing a
              banner can carry that a 220px card cannot. Everything else that was here (the
              refund sentence, the advice to try again) was explanation nobody had asked for
              yet; the refund is on the receipt and the retry is obvious. Shown only when every
              failure on screen has the SAME reason: with two causes, one sentence is wrong
              about one of them. */}
          {(() => {
            const reasons = new Set(failed.map((j) => failureReasonFor(j.error)).filter(Boolean));
            if (reasons.size !== 1) return null;
            return (
              <p className="mt-1 text-[13px] leading-snug text-gray-600">
                {[...reasons][0]}
              </p>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={dismissAll}
          aria-label={t('ws_failed_dismiss')}
          title={t('ws_failed_dismiss')}
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100
                     hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <X className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

export default FailedJobBanner;
